import React, { useState } from "react";
import { Plus, Check, Star, Trash2, Copy, Users, Lock, ChevronDown, Edit2 } from "lucide-react";

export const PresetRail = ({
  presets = [],
  activePresetId,
  isModified,
  onSelectPreset,
  onResetToFull,
  onSaveNew,
  onUpdatePreset,
  onDeletePreset,
  canShareCompany,
  currentTheme,
  theme,
  isRTL,
  t,
  screenSize,
  // Real size of the "Full template" for the template being built.
  fullColumnCount = 186,
  fullTypeCount = 50,
  // "Default for …" choices: [{ key, templateKind, label: { en, ar } }]
  defaultPurposes = [],
  templateKind = "allowancesDeductions",
  onSetDefault, // async (presetId, defaultFor | null)
}) => {
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newScope, setNewScope] = useState("PERSONAL");
  const [newDefaultFor, setNewDefaultFor] = useState("");
  const [showDefaultMenu, setShowDefaultMenu] = useState(false);
  const purposeLabel = (p) => (p ? (isRTL ? p.label?.ar : p.label?.en) || p.key : "");
  const purposeByKey = (key) => defaultPurposes.find((p) => p.key === key);
  const compatiblePurposes = defaultPurposes.filter((p) => p.templateKind === templateKind);
  const defaultLabelOf = (preset) =>
    preset?.defaultFor ? purposeLabel(purposeByKey(preset.defaultFor)) : preset?.isDefault ? (t("presetDefault") || "Default") : "";
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [editingPresetId, setEditingPresetId] = useState(null);
  const [editName, setEditName] = useState("");

  const isMobile = screenSize === "xs" || screenSize === "sm";

  const handleStartCreate = () => {
    setNewName("");
    setNewDesc("");
    setNewScope("PERSONAL");
    setNewDefaultFor("");
    setFormError("");
    setShowNewForm(true);
  };

  const handleCancelCreate = () => {
    setShowNewForm(false);
    setFormError("");
  };

  const handleSubmitCreate = async (e) => {
    e?.preventDefault();
    if (!newName.trim()) {
      setFormError(t("presetNameRequired"));
      return;
    }
    setIsSaving(true);
    setFormError("");
    try {
      await onSaveNew({
        name: newName.trim(),
        description: newDesc.trim(),
        scope: newScope,
        defaultFor: newDefaultFor || null,
      });
      setShowNewForm(false);
    } catch (err) {
      setFormError(err.message || t("presetSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async (preset) => {
    try {
      await onSaveNew({
        name: `${preset.name} (${t("presetCopySuffix")})`,
        description: preset.description || "",
        scope: "PERSONAL",
        defaultFor: null,
      });
    } catch (err) {
      console.error("Duplicate failed:", err);
    }
  };

  const handleSetDefault = async (preset, defaultFor) => {
    setShowDefaultMenu(false);
    try {
      if (onSetDefault) await onSetDefault(preset.id, defaultFor);
      else await onUpdatePreset(preset.id, { defaultFor });
    } catch (err) {
      console.error("Set default failed:", err);
    }
  };

  const activePreset = presets.find((p) => p.id === activePresetId);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        width: isMobile ? "100%" : "260px",
        minWidth: isMobile ? "100%" : "220px",
        borderInlineEnd: isMobile ? "none" : `1px solid ${currentTheme.colors.borderLight}`,
        borderBottom: isMobile ? `1px solid ${currentTheme.colors.borderLight}` : "none",
        paddingInlineEnd: isMobile ? 0 : "18px",
        paddingBottom: isMobile ? "16px" : 0,
        overflowY: "auto",
        maxHeight: isMobile ? "240px" : "100%",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontSize: "0.85rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: currentTheme.colors.textMuted }}>
          {t("presets") || "PRESETS"}
        </div>
        {!showNewForm && (
          <button
            type="button"
            onClick={handleStartCreate}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: "transparent",
              border: `1px dashed ${currentTheme.colors.border}`,
              color: currentTheme.colors.primary,
              fontSize: "0.8rem",
              fontWeight: 600,
              padding: "4px 8px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            <Plus size={14} />
            <span>{t("newPreset") || "New"}</span>
          </button>
        )}
      </div>

      {/* Inline Create Form */}
      {showNewForm && (
        <form
          onSubmit={handleSubmitCreate}
          style={{
            background: theme === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
            border: `1px solid ${currentTheme.colors.primary}55`,
            borderRadius: "10px",
            padding: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          <div style={{ fontSize: "0.82rem", fontWeight: 700, color: currentTheme.colors.primary }}>
            {t("newPreset") || "New Preset"}
          </div>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("presetName") || "Preset name"}
            maxLength={60}
            required
            autoFocus
            style={{
              padding: "7px 10px",
              borderRadius: "6px",
              border: `1px solid ${currentTheme.colors.border}`,
              background: currentTheme.colors.inputBackground,
              color: currentTheme.colors.textPrimary,
              fontSize: "0.82rem",
            }}
          />
          <input
            type="text"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder={t("presetDescription") || "Description (optional)"}
            maxLength={200}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: `1px solid ${currentTheme.colors.borderLight}`,
              background: currentTheme.colors.inputBackground,
              color: currentTheme.colors.textPrimary,
              fontSize: "0.78rem",
            }}
          />
          {canShareCompany && (
            <div style={{ display: "flex", gap: "6px", fontSize: "0.75rem" }}>
              <button
                type="button"
                onClick={() => setNewScope("PERSONAL")}
                style={{
                  flex: 1,
                  padding: "4px",
                  borderRadius: "4px",
                  border: newScope === "PERSONAL" ? `1px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.border}`,
                  background: newScope === "PERSONAL" ? `${currentTheme.colors.primary}18` : "transparent",
                  color: newScope === "PERSONAL" ? currentTheme.colors.primary : currentTheme.colors.textSecondary,
                  cursor: "pointer",
                  fontWeight: newScope === "PERSONAL" ? 600 : 400,
                }}
              >
                {t("presetScopePersonal") || "Personal"}
              </button>
              <button
                type="button"
                onClick={() => setNewScope("COMPANY")}
                style={{
                  flex: 1,
                  padding: "4px",
                  borderRadius: "4px",
                  border: newScope === "COMPANY" ? `1px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.border}`,
                  background: newScope === "COMPANY" ? `${currentTheme.colors.primary}18` : "transparent",
                  color: newScope === "COMPANY" ? currentTheme.colors.primary : currentTheme.colors.textSecondary,
                  cursor: "pointer",
                  fontWeight: newScope === "COMPANY" ? 600 : 400,
                }}
              >
                {t("presetScopeCompany") || "Company"}
              </button>
            </div>
          )}
          <label style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "0.76rem", color: currentTheme.colors.textSecondary }}>
            <span>{t("presetDefaultFor") || "Default for"}</span>
            <select
              value={newDefaultFor}
              onChange={(e) => setNewDefaultFor(e.target.value)}
              style={{
                padding: "6px 8px",
                borderRadius: "6px",
                border: `1px solid ${currentTheme.colors.borderLight}`,
                background: theme === "dark" ? "#0f172a" : "#fff",
                color: currentTheme.colors.textPrimary,
                fontSize: "0.78rem",
              }}
            >
              <option value="">{t("presetNotDefault") || "Not a default"}</option>
              {compatiblePurposes.map((p) => (
                <option key={p.key} value={p.key}>{purposeLabel(p)}</option>
              ))}
            </select>
          </label>
          {formError && (
            <div style={{ fontSize: "0.74rem", color: currentTheme.colors.danger }}>
              {formError}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
            <button
              type="button"
              onClick={handleCancelCreate}
              style={{
                padding: "4px 8px",
                borderRadius: "5px",
                border: `1px solid ${currentTheme.colors.border}`,
                background: "transparent",
                color: currentTheme.colors.textSecondary,
                fontSize: "0.75rem",
                cursor: "pointer",
              }}
            >
              {t("cancel") || "Cancel"}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              style={{
                padding: "4px 10px",
                borderRadius: "5px",
                border: "none",
                background: currentTheme.colors.primary,
                color: "#fff",
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: isSaving ? "not-allowed" : "pointer",
              }}
            >
              {isSaving ? t("saving") : t("savePreset")}
            </button>
          </div>
        </form>
      )}

      {/* Full Template Preset Item */}
      <div
        onClick={onResetToFull}
        style={{
          padding: "10px 12px",
          borderRadius: "10px",
          cursor: "pointer",
          border: activePresetId === null ? `1.5px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.borderLight}`,
          background: activePresetId === null ? `${currentTheme.colors.primary}12` : "transparent",
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          transition: "all 0.15s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600, fontSize: "0.86rem", color: currentTheme.colors.textPrimary }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: activePresetId === null ? currentTheme.colors.primary : currentTheme.colors.border,
                display: "inline-block",
              }}
            />
            <span>{t("fullTemplate") || "Full template"}</span>
          </div>
          <span style={{ fontSize: "0.72rem", color: currentTheme.colors.textMuted, background: `${currentTheme.colors.border}44`, padding: "2px 6px", borderRadius: "10px" }}>
            {fullColumnCount} {t("columnsShort")}
          </span>
        </div>
        <div style={{ fontSize: "0.75rem", color: currentTheme.colors.textSecondary, paddingInlineStart: "16px" }}>
          {t("fullTemplateAllTypes").replace("{count}", fullTypeCount)}
        </div>
      </div>

      {/* User Presets List */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {presets.map((preset) => {
          const isActive = activePresetId === preset.id;
          const isCompany = preset.scope === "COMPANY";

          return (
            <div
              key={preset.id}
              onClick={() => onSelectPreset(preset)}
              style={{
                padding: "9px 12px",
                borderRadius: "10px",
                cursor: "pointer",
                border: isActive ? `1.5px solid ${currentTheme.colors.primary}` : `1px solid ${currentTheme.colors.borderLight}`,
                background: isActive ? `${currentTheme.colors.primary}10` : "transparent",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                transition: "all 0.15s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 600, fontSize: "0.84rem", color: currentTheme.colors.textPrimary }}>
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: isActive ? currentTheme.colors.primary : currentTheme.colors.border,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ maxWidth: "140px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {preset.name}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  {(preset.defaultFor || preset.isDefault) && (
                    <span title={`${t("presetDefaultFor") || "Default for"}: ${defaultLabelOf(preset)}`} style={{ display: "inline-flex" }}>
                      <Star size={12} fill={currentTheme.colors.warning} color={currentTheme.colors.warning} />
                    </span>
                  )}
                  {isCompany ? (
                    <Users size={12} color={currentTheme.colors.primary} />
                  ) : (
                    <Lock size={12} color={currentTheme.colors.textMuted} />
                  )}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingInlineStart: "16px", fontSize: "0.72rem", color: currentTheme.colors.textSecondary }}>
                <span>
                  {isCompany ? (t("presetScopeCompany") || "Company") : (t("presetScopePersonal") || "Personal")}
                </span>
                {isActive && isModified && (
                  <span style={{ color: currentTheme.colors.warning, fontWeight: 700 }}>
                    • {t("presetModified") || "Modified"}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Preset Action Toolbar for Active Preset */}
      {activePreset && (
        <div
          style={{
            marginTop: "auto",
            paddingTop: "10px",
            borderTop: `1px solid ${currentTheme.colors.borderLight}`,
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            position: "relative", // anchors the "Default for" menu
          }}
        >
          <button
            type="button"
            title={t("duplicatePreset") || "Duplicate"}
            onClick={() => handleDuplicate(activePreset)}
            style={{
              padding: "4px 8px",
              borderRadius: "6px",
              border: `1px solid ${currentTheme.colors.borderLight}`,
              background: "transparent",
              color: currentTheme.colors.textSecondary,
              fontSize: "0.72rem",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
            }}
          >
            <Copy size={12} />
            <span>{t("duplicatePreset") || "Duplicate"}</span>
          </button>
          {/* Make default → asks what it's the default for. */}
          <div>
            <button
              type="button"
              title={t("setAsDefault") || "Set Default"}
              onClick={() => setShowDefaultMenu((v) => !v)}
              style={{
                padding: "4px 8px",
                borderRadius: "6px",
                border: `1px solid ${currentTheme.colors.borderLight}`,
                background: "transparent",
                color: activePreset.defaultFor || activePreset.isDefault ? currentTheme.colors.warning : currentTheme.colors.textSecondary,
                fontSize: "0.72rem",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                cursor: "pointer",
              }}
            >
              <Star size={12} fill={activePreset.defaultFor || activePreset.isDefault ? currentTheme.colors.warning : "none"} />
              <span>
                {activePreset.defaultFor || activePreset.isDefault
                  ? `${t("presetDefaultFor") || "Default for"}: ${defaultLabelOf(activePreset)}`
                  : t("presetMakeDefault") || "Make Default"}
              </span>
              <ChevronDown size={11} />
            </button>
            {showDefaultMenu && (
              <div
                role="menu"
                style={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)",
                  insetInline: 0,
                  zIndex: 5,
                  padding: "6px",
                  borderRadius: "10px",
                  border: `1px solid ${currentTheme.colors.borderLight}`,
                  background: theme === "dark" ? "#0f172a" : "#fff",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                }}
              >
                <div style={{ padding: "4px 8px 6px", fontSize: "0.7rem", fontWeight: 700, color: currentTheme.colors.textMuted, textTransform: "uppercase" }}>
                  {t("presetDefaultFor") || "Default for"}
                </div>
                {defaultPurposes.map((p) => {
                  const compatible = p.templateKind === templateKind;
                  const active = activePreset.defaultFor === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      role="menuitemradio"
                      aria-checked={active}
                      disabled={!compatible}
                      onClick={() => handleSetDefault(activePreset, p.key)}
                      title={compatible ? "" : t("presetDefaultOtherTemplate") || "Built for another template"}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                        padding: "7px 8px",
                        border: "none",
                        borderRadius: "6px",
                        background: active ? `${currentTheme.colors.primary}14` : "transparent",
                        color: compatible ? currentTheme.colors.textPrimary : currentTheme.colors.textMuted,
                        opacity: compatible ? 1 : 0.55,
                        fontSize: "0.78rem",
                        textAlign: "start",
                        cursor: compatible ? "pointer" : "not-allowed",
                      }}
                    >
                      <span style={{ width: 12, display: "inline-flex" }}>{active && <Check size={12} />}</span>
                      <span style={{ flex: 1 }}>{purposeLabel(p)}</span>
                      {!compatible && (
                        <span style={{ fontSize: "0.66rem" }}>{t("presetDefaultOtherTemplate") || "Other template"}</span>
                      )}
                    </button>
                  );
                })}
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={!activePreset.defaultFor && !activePreset.isDefault}
                  onClick={() => handleSetDefault(activePreset, null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    marginTop: "4px",
                    padding: "7px 8px",
                    border: "none",
                    borderTop: `1px solid ${currentTheme.colors.borderLight}`,
                    background: "transparent",
                    color: currentTheme.colors.textSecondary,
                    fontSize: "0.78rem",
                    textAlign: "start",
                    cursor: "pointer",
                  }}
                >
                  <span style={{ width: 12, display: "inline-flex" }}>
                    {!activePreset.defaultFor && !activePreset.isDefault && <Check size={12} />}
                  </span>
                  <span>{t("presetNotDefault") || "Not a default"}</span>
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            title={t("deletePreset") || "Delete"}
            onClick={() => {
              if (window.confirm(t("confirmDeletePreset") || "Delete this preset? This cannot be undone.")) {
                onDeletePreset(activePreset.id);
              }
            }}
            style={{
              padding: "4px 8px",
              borderRadius: "6px",
              border: `1px solid ${currentTheme.colors.danger}44`,
              background: "transparent",
              color: currentTheme.colors.danger,
              fontSize: "0.72rem",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
            }}
          >
            <Trash2 size={12} />
            <span>{t("deletePreset") || "Delete"}</span>
          </button>
        </div>
      )}
    </div>
  );
};
